import type { RGNEvent } from '@/lib/api/schemas'

/**
 * Tournament Stadium — atmospheric game backdrop.
 *
 * Renders the RAWG `backgroundImage` (baked into `event.gameBackgroundUrl`
 * at create time by the events Lambda's getCachedGameMeta()) as a
 * dimmed, vignette-overlaid hero image behind the phase content.
 *
 * Two intensity modes:
 *   - `centered` (default, TEASER + CHECK_IN + COMPLETE): heavy
 *     vignette so the centred text composition reads cleanly.
 *   - `paneled` (LIVE + FINAL_MATCH): looser vignette + higher base
 *     opacity because the foreground composition is two large panels
 *     that already provide their own contrast. Without this, the
 *     backdrop reads as "invisible" once panels cover ~85% of the
 *     canvas.
 *
 * Returns `null` when the event has no `gameBackgroundUrl` — caller
 * doesn't need to gate. The flat TV palette behind it reads fine on
 * its own.
 */
export function GameBackdrop({
  event,
  mode = 'centered',
}: {
  event: RGNEvent
  mode?: 'centered' | 'paneled'
}) {
  const src = event.gameBackgroundUrl
  if (!src) return null
  const isPaneled = mode === 'paneled'
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <img
        src={src}
        alt=""
        loading="eager"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
        style={{
          // LIVE phase: brighter base so the backdrop survives through
          // 78%-opaque panels. TEASER / centered modes stay subtle.
          opacity: isPaneled ? 0.45 : 0.22,
          transform: 'scale(1.06)',
          filter: isPaneled ? 'saturate(1.1)' : 'saturate(1.05)',
        }}
      />
      {/* Radial vignette. Paneled mode runs ~half as dark so the
          gaps between panels actually show the image; centered mode
          stays heavy to protect text on the empty canvas. */}
      <div
        className="absolute inset-0"
        style={{
          background: isPaneled
            ? 'radial-gradient(ellipse at center, rgba(6,6,15,0.30) 0%, rgba(6,6,15,0.55) 60%, rgba(6,6,15,0.78) 100%)'
            : 'radial-gradient(ellipse at center, rgba(6,6,15,0.78) 0%, rgba(6,6,15,0.88) 55%, rgba(6,6,15,0.96) 100%)',
        }}
      />
      {/* Bottom fade — only in centered mode where the fact strip sits.
          Paneled mode hides this under the ticker chrome anyway. */}
      {!isPaneled && (
        <div
          className="absolute right-0 bottom-0 left-0"
          style={{
            height: '40%',
            background:
              'linear-gradient(to top, rgba(6,6,15,0.85), rgba(6,6,15,0))',
          }}
        />
      )}
    </div>
  )
}

/**
 * Companion: render the game name + genre / release year line. Used as
 * a subtle subtitle under the tournament title where it adds context.
 * Drops cleanly when the event has no `gameName`.
 */
export function GameSubtitle({ event }: { event: RGNEvent }) {
  const name = event.gameName?.trim()
  if (!name) return null
  return (
    <p
      className="font-display font-medium uppercase"
      style={{
        color: 'var(--tv-fg-muted)',
        letterSpacing: '0.22em',
        fontSize: 'clamp(0.85rem, 1vw, 1.1rem)',
        marginTop: 'clamp(4px, 0.6vh, 12px)',
      }}
    >
      {name}
    </p>
  )
}
